import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "./Toast/toast.ts";
import moment from "moment-timezone";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Calendar,
  Store,
  FileText,
  MessageSquare,
  AlertTriangle,
  ShieldAlert,
  CheckCircle2,
  MinusCircle,
  Clock,
  Tag,
  Shirt,
  Package,
  Copy,
  Share2,
  ChevronRight,
  Send,
  ImageIcon,
  Video,
  Eye,
  X,
  TrendingUp,
  Hash,
} from "lucide-react";
import { RWebShare } from "react-web-share";
import ReportsPageRadioButton from "./ReportsPageRadioButton.tsx";
import ReportsPageCategoryRadioBtn from "./ReportsPageCategoryRadioBtn.tsx";
import CustomVideoPlayer from "../components/CustomVideoPlayer/CustomVideoPlayerForDetailsPage.tsx";
import CustomImage from "./CustomImg/CustomImage.tsx";
import { useNotificationContext } from "../context/NotificationContext.tsx";
import DescriptionBox from "../components/DescriptionBox/index.tsx";

/* ─── Severity Config ─── */
const SEV_CONFIG = {
  High: {
    icon: AlertTriangle,
    color: "text-[rgb(var(--danger))]",
    bg: "bg-[rgb(var(--danger)/0.08)]",
    border: "border-[rgb(var(--danger)/0.25)]",
    dot: "bg-[rgb(var(--danger))]",
    label: "High",
  },
  Medium: {
    icon: ShieldAlert,
    color: "text-[rgb(var(--warning))]",
    bg: "bg-[rgb(var(--warning)/0.08)]",
    border: "border-[rgb(var(--warning)/0.25)]",
    dot: "bg-[rgb(var(--warning))]",
    label: "Medium",
  },
  Low: {
    icon: MinusCircle,
    color: "text-[rgb(var(--success))]",
    bg: "bg-[rgb(var(--success)/0.08)]",
    border: "border-[rgb(var(--success)/0.25)]",
    dot: "bg-[rgb(var(--success))]",
    label: "Low",
  },
};

/* ─── Status Badge ─── */
const StatusBadge = ({ status }: { status: string }) => {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    Approved: { label: "Approved", color: "text-[rgb(var(--success))]", bg: "bg-[rgb(var(--success)/0.1)]" },
    Pending: { label: "Pending", color: "text-[rgb(var(--warning))]", bg: "bg-[rgb(var(--warning)/0.1)]" },
    Declined: { label: "Declined", color: "text-[rgb(var(--danger))]", bg: "bg-[rgb(var(--danger)/0.1)]" },
  };
  const cfg = map[status] || { label: status || "—", color: "text-[rgb(var(--text-muted))]", bg: "bg-[rgb(var(--surface-3))]" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${cfg.color} ${cfg.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.bg.replace("bg-", "bg-").replace("/0.1", "")} inline-block`} />
      {cfg.label}
    </span>
  );
};

/* ─── Info Row ─── */
const InfoRow = ({
  icon: Icon,
  label,
  value,
  children,
}: {
  icon: any;
  label: string;
  value?: string;
  children?: React.ReactNode;
}) => (
  <div className="flex items-start gap-3 py-3.5 border-b border-[rgb(var(--border-primary))] last:border-b-0">
    <div className="w-7 h-7 rounded-md bg-[rgb(var(--surface-3))] flex items-center justify-center shrink-0 mt-0.5">
      <Icon size={13} className="text-gold-2" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--text-muted))] mb-0.5">{label}</p>
      {children ?? (
        <p className="text-[13px] text-[rgb(var(--text-primary))] font-medium leading-snug">{value || "—"}</p>
      )}
    </div>
  </div>
);

/* ─── Section Card ─── */
const SectionCard = ({
  title,
  icon: Icon,
  children,
  className = "",
  headerRight,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
  className?: string;
  headerRight?: React.ReactNode;
}) => (
  <div className={`border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] overflow-hidden ${className}`}>
    <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-[rgb(var(--border-primary))] bg-[rgb(var(--surface-2)/0.5)]">
      <div className="w-6 h-6 rounded-md bg-gold-2/10 flex items-center justify-center">
        <Icon size={12} className="text-gold-2" />
      </div>
      <span className="text-[13px] font-semibold text-[rgb(var(--text-primary))]">{title}</span>
      {headerRight && <div className="ml-auto">{headerRight}</div>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

/* ─── Skeleton ─── */
const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`rounded-md bg-[rgb(var(--surface-3))] animate-pulse ${className}`} />
);

const LoadingSkeleton = () => (
  <div className="flex flex-col gap-6">
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 flex flex-col gap-4">
        <div className="border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] p-5 flex flex-col gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="w-7 h-7 shrink-0" />
              <div className="flex-1">
                <Skeleton className="h-2.5 w-16 mb-1.5" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-2 flex flex-col gap-4">
        <div className="border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] p-5">
          <Skeleton className="h-4 w-24 mb-4" />
          <Skeleton className="h-28 w-full" />
        </div>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const ReportDetails: React.FC = () => {
  const { _id } = useParams();
  const navigate = useNavigate();
  const { readNotification } = useNotificationContext();

  const [reportData, setReportData] = useState<any>(null);
  const storedToken = localStorage.getItem("token");
  const [commentsVal, setCommentsVal] = useState("");
  const [reload, setReload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  /* Modals */
  const [approveModal, setApproveModal] = useState(false);
  const [declineModal, setDeclineModal] = useState(false);
  const [cashierActivity, setCashierActivity] = useState("");

  /* Media */
  const [imgHeader, setImgHeader] = useState("");
  const [imgVisible, setImgVisible] = useState(false);
  const [imgSrc, setImgSrc] = useState("");

  /* Helpers */
  const formatCommentDate = (d: string) => moment(d).format("MMM DD, YYYY · hh:mm A");
  const formatDate = (d: string) => (d ? moment(d).format("MMM DD, YYYY") : "—");
  const formatTime = (d: string, tz: string) => (d ? moment.tz(d, tz).format("hh:mm A") : "—");
  const formatString = (str: string) => {
    if (!str) return "—";
    return str.split(/(?=[A-Z])/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url).then(
      () => toast.success("URL copied to clipboard"),
      (e) => toast.error(e?.message)
    );
  };

  /* Fetch */
  const getReportDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/cust-portal/customer/report/${_id}`,
        { headers: { authorization: `Bearer ${storedToken}` } }
      );
      setReportData(response?.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || "Unable to retrieve report data!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getReportDetails();
    // eslint-disable-next-line
  }, [reload, readNotification]);

  useEffect(() => {
    if (!imgVisible) return;
    try {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({ event: "image_opened", imageHeader: imgHeader || "(unknown)", storeName: reportData?.store_name });
      (window as any).dataLayer.push({ imageHeader: undefined, storeName: undefined });
    } catch {}
  }, [imgVisible, imgHeader]);

  /* Actions */
  const handleAddComment = async () => {
    if (!commentsVal.trim()) return;
    try {
      setSubmittingComment(true);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/cust-portal/customer/comment`,
        { id: _id, payload: commentsVal },
        { headers: { Authorization: `Bearer ${storedToken}` } }
      );
      toast.success(res?.data?.message);
      setCommentsVal("");
      setReload((r) => !r);
    } catch {
      toast.error("An error occurred while adding comment. Please try again!");
    } finally {
      setSubmittingComment(false);
    }
  };

  const updateStatus = async (status: string) => {
    setApproveModal(false);
    setDeclineModal(false);
    setLoading(true);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/cashierIncentive/update`,
        { cashier_status: status, _id },
        { headers: { Authorization: `Bearer ${storedToken}` } }
      );
      toast.success(res?.data?.message);
      setReload((r) => !r);
    } catch {
      toast.error("An error occurred while updating status. Please try again!");
    } finally {
      setLoading(false);
    }
  };

  /* Derived */
  const {
    report_date, store_name, report, sev_level, cust_comments,
    incentive_category, report_status, cashier_status,
    products, incident_timings, dress, category, store_id,
  } = reportData || {};

  const sevCfg = SEV_CONFIG[sev_level as keyof typeof SEV_CONFIG];
  const SevIcon = sevCfg?.icon ?? MinusCircle;

  const validMedia = (reportData?.media || []).filter(Boolean);
  const images = validMedia.filter((m: any) => !m.file_url?.endsWith(".mp4"));
  const videos = validMedia.filter((m: any) => m.file_url?.endsWith(".mp4"));

  const tz = store_id?.timezone || "America/Los_Angeles";

  /* ── render ── */
  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg border border-[rgb(var(--border-primary))] bg-[rgb(var(--surface-1))] hover:bg-[rgb(var(--surface-2))] text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-primary))] transition-colors"
        >
          <ArrowLeft size={15} />
        </button>
        <DescriptionBox
          title="POS Loss Prevention"
          subTitle="Report Details"
          description="Full detail view for this cashier activity report."
        />
        {report_status && !loading && (
          <div className="ml-auto">
            <StatusBadge status={report_status} />
          </div>
        )}
      </div>

      {/* ── Image Dialog ── */}
      <Dialog
        header={imgHeader}
        visible={imgVisible}
        maximized
        style={{ width: "100vw" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        onHide={() => setImgVisible(false)}
      >
        <div className="flex items-center justify-center w-full h-full">
          <CustomImage src={imgSrc} alt={imgHeader} imageName={imgHeader} />
        </div>
      </Dialog>

      {/* ── Approve Modal ── */}
      <Dialog
        header="Confirm Approval"
        visible={approveModal}
        style={{ width: "460px" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        onHide={() => setApproveModal(false)}
        footer={
          <div className="flex gap-2 justify-end pt-2">
            <button
              onClick={() => setApproveModal(false)}
              className="px-4 py-2 rounded-md border border-[rgb(var(--border-secondary))] bg-[rgb(var(--surface-2))] text-[rgb(var(--text-secondary))] text-[13px] hover:bg-[rgb(var(--surface-3))] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => updateStatus(cashierActivity)}
              className="px-4 py-2 rounded-md bg-gold-2 hover:bg-gold-3 text-white text-[13px] font-medium transition-colors"
            >
              Confirm
            </button>
          </div>
        }
      >
        <ReportsPageRadioButton cashierActivity={cashierActivity} setCashierActivity={setCashierActivity} />
      </Dialog>

      {/* ── Decline Modal ── */}
      <Dialog
        header="Confirm Decline"
        visible={declineModal}
        style={{ width: "400px" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        onHide={() => setDeclineModal(false)}
        footer={
          <div className="flex gap-2 justify-end pt-2">
            <button
              onClick={() => setDeclineModal(false)}
              className="px-4 py-2 rounded-md border border-[rgb(var(--border-secondary))] bg-[rgb(var(--surface-2))] text-[rgb(var(--text-secondary))] text-[13px] hover:bg-[rgb(var(--surface-3))] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => updateStatus("declined")}
              className="px-4 py-2 rounded-md bg-[rgb(var(--danger))] hover:opacity-90 text-white text-[13px] font-medium transition-colors"
            >
              Decline Report
            </button>
          </div>
        }
      >
        <div className="flex items-start gap-3 p-1">
          <div className="w-10 h-10 rounded-full bg-[rgb(var(--danger)/0.1)] flex items-center justify-center shrink-0">
            <AlertTriangle size={18} className="text-[rgb(var(--danger))]" />
          </div>
          <div>
            <p className="text-[14px] font-semibold text-[rgb(var(--text-primary))] mb-1">Are you sure?</p>
            <p className="text-[12px] text-[rgb(var(--text-muted))] leading-relaxed">
              This action will mark the report as declined. This cannot be undone without administrator help.
            </p>
          </div>
        </div>
      </Dialog>

      {/* ── Body ── */}
      {loading ? (
        <LoadingSkeleton />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        >
          {/* ── LEFT COLUMN ── */}
          <div className="lg:col-span-1 flex flex-col gap-4">

            {/* Core Info */}
            <SectionCard title="Report Info" icon={FileText}>
              <InfoRow icon={Hash} label="Report ID" value={_id?.toString().slice(-8).toUpperCase()} />
              <InfoRow icon={Calendar} label="Date">
                <p className="text-[13px] font-medium text-[rgb(var(--text-primary))]">
                  {formatDate(report_date)}
                </p>
              </InfoRow>
              <InfoRow icon={Store} label="Store" value={store_name} />
            </SectionCard>

            {/* Severity */}
            <SectionCard title="Severity" icon={AlertTriangle}>
              {sev_level ? (
                <motion.div
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  className={`flex items-center gap-3 p-4 rounded-lg border ${sevCfg?.bg} ${sevCfg?.border}`}
                >
                  <div className={`w-10 h-10 rounded-full ${sevCfg?.bg} border ${sevCfg?.border} flex items-center justify-center shrink-0`}>
                    <SevIcon size={18} className={sevCfg?.color} />
                  </div>
                  <div>
                    <p className={`text-[15px] font-bold ${sevCfg?.color}`}>{sevCfg?.label}</p>
                    <p className="text-[11px] text-[rgb(var(--text-muted))] mt-0.5">Severity Level</p>
                  </div>
                  <div className={`ml-auto w-2 h-2 rounded-full ${sevCfg?.dot} animate-pulse`} />
                </motion.div>
              ) : (
                <p className="text-[13px] text-[rgb(var(--text-muted))]">Not assessed yet</p>
              )}
            </SectionCard>

            {/* Cashier Status (conditional) */}
            {report_status === "Approved" && incentive_category === "cashier" && sev_level === "High" && (
              <SectionCard title="Cashier Status" icon={TrendingUp}>
                <div className="flex flex-col gap-3">
                  <div className="px-3 py-2.5 rounded-md bg-[rgb(var(--surface-2))] border border-[rgb(var(--border-primary))]">
                    <p className="text-[11px] text-[rgb(var(--text-muted))] mb-0.5">Current Status</p>
                    <p className="text-[13px] font-semibold text-[rgb(var(--text-primary))]">
                      {formatString(cashier_status)}
                    </p>
                  </div>
                  <div className="rounded-md bg-[rgb(var(--surface-2))] border border-[rgb(var(--border-primary))] p-3 text-[11px] text-[rgb(var(--text-secondary))] leading-relaxed flex flex-col gap-1.5">
                    <p className="text-[11px] font-bold text-warning uppercase tracking-wide mb-0.5">Cashier Suspicious</p>
                    <p className="page-subtitle">i. If cashier has consumed anything from the store than :- It will fall under <b>Cashier Suspicious</b> Activity.</p>
                    <p className="page-subtitle">ii. If cashier is giving products to the customers without paying\scanning.</p>
                    <p className="text-[11px] font-bold text-[rgb(var(--danger))] uppercase tracking-wide mt-1 mb-0.5">Cashier Fraud</p>
                    <p className="page-subtitle">Billing, Cash & Transaction related reports are approved under <b>Cashier Fraud</b> Activity.</p>
                  </div>
                  <div className="flex gap-2 mt-1">
                    <button
                      onClick={() => setApproveModal(true)}
                      className="flex-1 h-[38px] rounded-md bg-gold-2 hover:bg-gold-3 text-white text-[12px] font-medium transition-colors flex items-center justify-center gap-1.5"
                    >
                      <CheckCircle2 size={13} /> Approve
                    </button>
                    <button
                      onClick={() => setDeclineModal(true)}
                      className="flex-1 h-[38px] rounded-md bg-[rgb(var(--surface-3))] hover:bg-[rgb(var(--danger)/0.1)] border border-[rgb(var(--border-secondary))] hover:border-[rgb(var(--danger)/0.3)] text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--danger))] text-[12px] font-medium transition-all flex items-center justify-center gap-1.5"
                    >
                      <X size={13} /> Decline
                    </button>
                  </div>
                </div>
              </SectionCard>
            )}
          </div>

          {/* ── RIGHT COLUMN ── */}
          <div className="lg:col-span-2 flex flex-col gap-4">

            {/* Report Body (Approved only) */}
            {report_status === "Approved" && (
              <SectionCard title="Incident Report" icon={FileText}>
                <p className="text-[13px] text-[rgb(var(--text-secondary))] leading-relaxed whitespace-pre-wrap">
                  {report || "No report details available."}
                </p>
              </SectionCard>
            )}

            {/* Extra Details (High + not approved) */}
            {report_status !== "Approved" && sev_level === "High" && (
              <SectionCard title="Incident Details" icon={FileText}>
                <div className="flex flex-col divide-y divide-[rgb(var(--border-primary))]">
                  <div className="pb-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--text-muted))] mb-2.5">Category</p>
                    <ReportsPageCategoryRadioBtn category={category} />
                  </div>
                  <div className="grid grid-cols-2 gap-4 py-4">
                    <InfoRow icon={Shirt} label="Dress" value={dress} />
                    <InfoRow icon={Package} label="Products" value={products} />
                  </div>
                  {incident_timings && (
                    <div className="pt-4">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--text-muted))] mb-3">Incident Timings</p>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: "Pickup", value: formatTime(incident_timings?.pickup_time, tz) },
                          { label: "Hide", value: formatTime(incident_timings?.hide_time, tz) },
                          { label: "Exit", value: formatTime(incident_timings?.exit_time, tz) },
                        ].map(({ label, value }) => (
                          <div key={label} className="flex flex-col items-center gap-1 p-3 rounded-lg bg-[rgb(var(--surface-2))] border border-[rgb(var(--border-primary))]">
                            <Clock size={14} className="text-gold-2" />
                            <p className="text-[14px] font-semibold text-[rgb(var(--text-primary))] font-mono">{value}</p>
                            <p className="text-[10px] text-[rgb(var(--text-muted))] uppercase tracking-wide">{label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

            {/* Media (Approved only) */}
            {report_status === "Approved" && (
              <SectionCard
                title="Media Evidence"
                icon={ImageIcon}
                headerRight={
                  <span className="text-[11px] text-[rgb(var(--text-muted))] bg-[rgb(var(--surface-2))] px-2 py-0.5 rounded-full">
                    {validMedia.length} file{validMedia.length !== 1 ? "s" : ""}
                  </span>
                }
              >
                {validMedia.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-2">
                    <ImageIcon size={28} className="text-[rgb(var(--text-muted))] opacity-30" />
                    <p className="text-[12px] text-[rgb(var(--text-muted))]">No media attached</p>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-4">
                    {/* Videos */}
                    {videos.map((media: any, i: number) => (
                      <div key={i} className="max-w-[400px] rounded-lg border border-[rgb(var(--border-primary))] bg-[rgb(var(--surface-2))] overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[rgb(var(--border-primary))]">
                          <div className="flex items-center gap-2">
                            <Video size={13} className="text-gold-2" />
                            <span className="text-[12px] font-medium text-[rgb(var(--text-primary))] truncate max-w-[200px]">
                              {media?.file_name}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => copyToClipboard(media?.file_url)}
                              className="p-1.5 rounded-md hover:bg-[rgb(var(--surface-3))] text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-primary))] transition-colors"
                              title="Copy URL"
                            >
                              <Copy size={13} />
                            </button>
                            <RWebShare data={{ text: `Check out this media: ${media?.file_name}`, url: media?.file_url, title: "Share Media" }}>
                              <button
                                className="p-1.5 rounded-md hover:bg-[rgb(var(--surface-3))] text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-primary))] transition-colors"
                                title="Share"
                              >
                                <Share2 size={13} />
                              </button>
                            </RWebShare>
                          </div>
                        </div>
                        <div className="p-3">
                          <CustomVideoPlayer src={media?.file_url} videoName={media?.file_name} />
                        </div>
                      </div>
                    ))}

                    {/* Images */}
                    {images.length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--text-muted))] mb-2.5">
                          Images ({images.length})
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {images.map((media: any, i: number) => (
                            <motion.div
                              key={i}
                              whileHover={{ scale: 1.02 }}
                              transition={{ duration: 0.15 }}
                              className="relative rounded-lg overflow-hidden border border-[rgb(var(--border-primary))] cursor-pointer group aspect-video bg-[rgb(var(--surface-3))]"
                              onClick={() => {
                                setImgSrc(media?.file_url);
                                setImgHeader(media?.file_name);
                                setImgVisible(true);
                              }}
                            >
                              <img src={media?.file_url} alt={media?.file_name} className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                                <Eye size={18} className="text-white" />
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                <p className="text-[10px] text-white truncate">{media?.file_name}</p>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </SectionCard>
            )}

            {/* Comments History */}
            <SectionCard
              title="Comments History"
              icon={MessageSquare}
              headerRight={
                <span className="text-[11px] text-[rgb(var(--text-muted))] bg-[rgb(var(--surface-2))] px-2 py-0.5 rounded-full">
                  {cust_comments?.length || 0}
                </span>
              }
            >
              <div className="flex flex-col gap-3">
                {cust_comments && cust_comments.length > 0 ? (
                  <div className="flex flex-col gap-2 max-h-[280px] overflow-y-auto pr-1">
                    <AnimatePresence>
                      {cust_comments.map((item: any, index: number) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.04, duration: 0.25 }}
                          className="flex gap-3 p-3 rounded-lg bg-[rgb(var(--surface-2))] border border-[rgb(var(--border-primary))]"
                        >
                          <div className="w-7 h-7 rounded-full bg-gold-2/15 flex items-center justify-center shrink-0 text-[11px] font-bold text-gold-2">
                            {item?.comment_by?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[12px] font-semibold text-[rgb(var(--text-primary))]">
                                {item?.comment_by || "Unknown"}
                              </span>
                              <span className="text-[10px] text-[rgb(var(--text-muted))]">
                                {formatCommentDate(item?.comment_date)}
                              </span>
                            </div>
                            <p className="text-[12px] text-[rgb(var(--text-secondary))] leading-relaxed">
                              {item?.comment}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 gap-2">
                    <MessageSquare size={24} className="text-[rgb(var(--text-muted))] opacity-30" />
                    <p className="text-[12px] text-[rgb(var(--text-muted))]">No comments yet</p>
                  </div>
                )}

                {/* Add Comment */}
                <div className="border-t border-[rgb(var(--border-primary))] pt-4 mt-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--text-muted))] mb-2">
                    Add Comment
                  </p>
                  <div className="flex flex-col gap-2">
                    <textarea
                      value={commentsVal}
                      onChange={(e) => setCommentsVal(e.target.value)}
                      placeholder="Type your comment here..."
                      rows={3}
                      className="w-full px-3 py-2.5 text-[13px] rounded-md border border-[rgb(var(--border-secondary))] bg-[rgb(var(--surface-2))] text-[rgb(var(--text-primary))] placeholder:text-[rgb(var(--text-muted))] outline-none focus:border-gold-2 focus:ring-1 focus:ring-gold-2/20 transition-all resize-none"
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={handleAddComment}
                        disabled={!commentsVal.trim() || submittingComment}
                        style={{background: "var(--gold-grad)"}}
                        className="flex items-center gap-2 px-4 h-[38px] rounded-md hover:bg-gold-3 text-white text-[12px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {submittingComment ? (
                          <ProgressSpinner style={{ width: "16px", height: "16px" }} strokeWidth="3" animationDuration=".5s" />
                        ) : (
                          <Send size={13} />
                        )}
                        Submit Comment
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </SectionCard>

          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ReportDetails;